import * as React from 'react';

import {TooltipAttrs} from 'common/components/tooltip/types';
import {SectionItem} from 'common/components/items-menu/types';
import {ReportName} from 'common/report/constants';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {Translatable} from 'common/i18n';

export type NewReportSectionItem = SectionItem<ReportName>;

type NavigationLinkItemValue = ReportName | `discover`;

export type NavigationLinkItem = {
  icon: string;
  label: string;
  value: NavigationLinkItemValue;
  isDisabled?: boolean;
  tooltip?: TooltipAttrs;
  onClick?: () => void;
  href: string;
  tag?: string;
  isSelectedFn?: (props: {currentUrl: URL; itemUrl: URL}) => boolean;
};

export enum MenuType {
  CreateProjectModal = `createProjectModal`,
}

export type UrlLocation = {
  pathname: string;
  search: string;
  hash: string;
};

export type GlobalSidenavProps = {
  className?: string;
  location: UrlLocation;
};

export type GlobalSidenavLayoutProps = React.PropsWithChildren;

export enum TrackingEvent {
  ProjectSwitcherOpened = `Project Switcher Opened`,
  NewReportMenuOpened = `New Report Menu Opened`,
  NewReportCreated = `New Report Created`,
  SearchModalOpened = `Search Modal Opened`,
  AppLinkClicked = `App Link Clicked`,
  FooterMenuOpened = `Footer Menu Opened`,
  FooterMenuItemSelected = `Footer Menu Item Selected`,
  ExpansionToggled = `Expansion Toggled`,
  Mounted = `Mounted`,
}

export enum FooterMenuType {
  Settings = `settings`,
  Help = `help`,
  OverflowApps = `overflow-apps`,
}

export enum FooterMenuItemType {
  /** settings menu */
  Settings = `settings`,
  Language = `language`,
  Appearance = `appearance`,
  ImplementMixpanel = `implement-mixpanel`,
  BillingPlan = `billing-plan`,
  InviteUsers = `invite-users`,
  BackToSignup = `back-to-signup`,
  StartupCreditPromo = `startup-credit-promo`,

  /** help menu */
  Documentation = `documentation`,
  Changelog = `changelog`,
  DemoProjects = `demo-projects`,
  SubmitFeedback = `submit-feedback`,
  LiveChat = `live-chat`,
  ContactSupport = `contact-support`,
  ContactSales = `contact-sales`,
  ExternalLink = `external-link`,
}

export type ReportPersistenceCookie = {
  project: number;
  workspace?: number;
  report_path: string;
};

export type ReportContext = {
  projectId: number;
  project: CurrentProject;
  isProjectOrGlobalWorkspace: boolean;
};

export enum NavCategoryType {
  Analysis = `Analysis`,
  Applications = `Applications`,
  Dashboards = `Boards`,
  DataManagement = `Data Management`,
  Events = `Events`,
  Project = `Project`,
  Settings = `Settings`,
  Users = `Users`,
}

export interface NavReport {
  name?: string;
  description: Translatable;
  endpoint: string;
  /**
   * endpointAlias specifies that the different urls belong to the same report.
   * can be used when the report is being migrated to a new endpoint under a feature switch.
   */
  endpointAliases?: string[];
  icon: string;
  label: Translatable;
  beta?: boolean;
  hidden?: boolean;
  category?: NavCategoryType;
}

type DisabledReportConfig = {
  isDisabled: (reportContext: ReportContext) => boolean;
  disabledMsg: Translatable;
};

export interface NavReportConfig extends Omit<NavReport, `endpoint` | `hidden`> {
  hidden?: (reportContext: ReportContext) => boolean;
  endpoint: (reportContext: ReportContext) => string;
  disabling?: DisabledReportConfig;
}
